import { useEffect, useCallback, useRef } from "preact/hooks";
import { low_res_placeholder, wordpress_big_image, magnification } from "../settings";

import useMeowLightboxContext from "../context";
import { useController } from "../helpers";
import { HighResImage } from "./HighResImage";
import { LowResImage } from "./LowResImage";

export const MeowLightboxSlider = ({ className }) => {
  // Reference to the slider element
  const sliderRef = useRef();

  // Destructuring context values
  const { images, imagesNumber, currentIndex, animatedSlide } = useMeowLightboxContext();

  // Destructuring methods from the controller
  const { onCloseClick, onTouchStart, onTouchMove, onTouchEnd, onKeyDown, onClickOnMap } = useController();

  // Constructing CSS class names for the thumbnails
  const thumbnailClassNames = ['mwl__slider__image__thumbnail', magnification.enabled ? '' : 'magnification_disabled']
    .filter(v => !!v)
    .join(' ');

  // Helper function to determine if a given index is close to the current image's index
  const indexIsCloseFromCurrent = useCallback(
    (index) => index === currentIndex
      || Math.abs(index - currentIndex) < 2
      || (currentIndex === 0 && index === imagesNumber - 1)
      || (currentIndex === imagesNumber - 1 && index === 0),
    [imagesNumber, currentIndex]
  );

  // Callback for handling the high-resolution image load event
  const onLoadHighRes = useCallback(() => {
    const sliderImageContainers = sliderRef.current?.childNodes;
    if (sliderImageContainers.length === 0) {
      return;
    }
    sliderImageContainers.forEach((container, index) => {
      if (index === currentIndex) {
        // Mark the high-resolution image container as visible
        container.classList.add('visible');
      }
    });
  });

  // Event listener for keydowns
  useEffect(() => {
    window.document.addEventListener("keydown", onKeyDown, false);
    return () => window.document.removeEventListener("keydown", onKeyDown, false);
  }, [onKeyDown]);

  // Handle visibility of the images based on the current index
  useEffect(() => {
    const sliderImageContainers = sliderRef.current?.childNodes;
    if (sliderImageContainers.length === 0) {
      return;
    }
    sliderImageContainers.forEach((container, index) => {
      if (index === currentIndex) {
        const highResImage = container.querySelector('.mwl__slider__image__thumbnail__high-res');
        // If the low-res placeholder is disabled or the high-res image is loaded, mark it as visible
        if (!low_res_placeholder || highResImage.complete) {
          container.classList.add('visible');
        }
      } else {
        // Otherwise, hide the container
        container.classList.remove('visible');
      }
    });
  }, [currentIndex]);

  // Handle slider's transform and transition for slide animations
  useEffect(() => {
    if (!sliderRef.current) return;
    sliderRef.current.style.transition = animatedSlide ? 'transform 1s ease' : 'none';
    sliderRef.current.style.transform = `translate3d(${-currentIndex * 100}%, 0, 0)`;
  }, [currentIndex, animatedSlide]);

  return (
    <div ref={sliderRef}
      className={`${className} ${currentIndex !== null && animatedSlide ? 'mwl-animate' : ''}`}
      onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
        
      {images.map((image, index) => {
        const isPreparing = indexIsCloseFromCurrent(index);
        return (
          <div key={image.id} className={`mwl__slider__image`} data-index={index}>
            <div className={thumbnailClassNames} onClick={onCloseClick}>
              <div className="mwl__slider__image__thumbnail__low-res-container" onClick={null}>
                <LowResImage image={image} index={index} isPreparing={isPreparing} />
              </div>
              <HighResImage image={image} isPreparing={isPreparing}
                onLoad={low_res_placeholder ? onLoadHighRes : null}
                useBigImage={wordpress_big_image}
              />
              <div id={`image-map-${index}`} className="image-map" onClick={onClickOnMap}></div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
